/**
 * Stale-chunk handling — AUTO-RELOAD CURRENTLY DISABLED.
 *
 * Intent (when re-enabled): a user has the app open, we deploy a new build,
 * and the new build's chunk hashes differ. The user's tab still references
 * the old hashes; navigating triggers a 404. An auto-reload recovers them.
 *
 * Why it's disabled: the recovery infinite-looped in environments where one
 * or more remoteEntry.js (e.g. metriq-ui) reliably 503s. Vite's
 * `__vitePreload` fires `vite:preloadError` for the failed modulepreload,
 * the handler reloads, the next boot reproduces the same 503, reloads
 * again, forever. A previous "one-shot per session" flag guard
 * (CHUNK_RELOAD_FLAG) didn't hold because attemptImportWithReload cleared
 * the flag on every successful lazy import, re-arming the trigger.
 *
 * Until we have either (a) a working bounded-retry mechanism or (b) a
 * proper UI prompt to ask the user to refresh, both paths LOG and let the
 * call site degrade gracefully:
 *   - attemptImportWithReload returns a never-resolving promise on chunk
 *     errors → calling Suspense stays in its fallback (PageLoader / shell
 *     skeleton). UX is the same "stuck loading" state, but no reload loop.
 *   - registerGlobalChunkReloadHandler's vite:preloadError listener no-ops
 *     beyond logging.
 *
 * To bring auto-reload back: pick a strategy that can't loop (e.g. a hard
 * session counter that caps at 1, NEVER cleared by lazy-import success).
 *
 * IMPORTANT: this module deliberately has zero framework imports so main.tsx
 * can register the boot-time handler without pulling React into the entry
 * chunk (which would break the MF bootstrap separation pattern).
 */

export const CHUNK_RELOAD_FLAG = 'e6:chunk-reload-attempted';

export const isChunkLoadError = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Unable to preload /i.test(msg) // Vite __vitePreload (module + CSS variants)
  );
};

// Pure helper used by lazyWithRetry. On a chunk-load error, logs and returns
// a never-resolving promise so the calling Suspense stays in its fallback
// state — no reload, no rethrow that would crash the React tree.
//
// `_reload` is kept in the signature for backward-compatibility with existing
// callers/tests; it is no longer invoked.
export async function attemptImportWithReload<T>(
  factory: () => Promise<T>,
  _reload: () => void = () => window.location.reload()
): Promise<T> {
  try {
    return await factory();
  } catch (err) {
    if (isChunkLoadError(err)) {
      console.warn(
        '[chunk-reload] Lazy import failed (auto-reload muted to prevent loops). Please refresh.',
        err
      );
      // Never-resolving promise keeps the calling Suspense in its fallback
      // instead of bubbling the rejection past the (absent) ErrorBoundary
      // and crashing the React tree.
      return new Promise<T>(() => {});
    }
    throw err;
  }
}

// Vite-emitted event payload — typed locally because @types isn't shipped.
type ViteErrorEvent = Event & { payload?: unknown };

/**
 * Registers a vite:preloadError listener that LOGS only. No reload.
 * See the file docstring for why auto-reload is currently disabled.
 *
 * `_reload` is kept for backward-compatibility with existing tests; it is
 * no longer invoked.
 */
export function registerGlobalChunkReloadHandler(opts?: { reload?: () => void }): void {
  // Reference the opt to keep the call-site shape stable for callers
  // that still pass it; it's not used.
  void opts;

  window.addEventListener('vite:preloadError', (event: Event) => {
    const evt = event as ViteErrorEvent;
    console.warn(
      '[chunk-reload] vite:preloadError fired (auto-reload muted to prevent loops).',
      evt.payload
    );
    evt.preventDefault?.(); // tell Vite we handled it; don't throw
  });
}
